import React from 'react'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { useDateFormatter } from '../generic/hooks/useDateFormatter'
import { EditRounded, PushPinRounded, TuneRounded } from '@mui/icons-material'
import { EVENT_DIALOG_TABS, EVENT_DIALOG_TAB_KEYS } from './utils/eventDialogTabs'
import { waitJobLabel, waitOrderLabel, waitTechnologyLabel } from './utils/waitBoxLabel'
import './calendarSidePanel.css'

/**
 * The detail of the selected calendar entry, as a panel beside the calendar instead of a tooltip
 * over it.
 *
 * It shows EXACTLY what `CalendarTooltip` showed -- not one field more. What changes is the
 * arrangement: the fourteen rows of a job become four groups, each answering one question, and the
 * position inside the workflow stops being a line of text and becomes previous · this · next with
 * "step n of N" underneath.
 *
 * It covers the same three shapes the tooltip covered, so no entry loses its detail:
 *   - a job (`extendedProps.jobEvent`), the rich case;
 *   - a wait (`extendedProps.wait`), a table of the queued items;
 *   - anything else -- an unavailable device or a forecast -- which carries only an event name and
 *     its dates.
 *
 * ⚠️ It is opened by CLICK and closed with its own button, unlike the tooltip it replaces, which
 * followed the pointer. Two reasons, and both are binding: a panel that resizes the calendar on
 * hover makes the grid jump while the user is reading it, and the standing design criterion for this
 * repo is that nothing may depend on hover to work -- the operator reads this standing at the
 * machine, with gloves on.
 */

/**
 * One icon per action, so the three tiles are told apart at a glance from across the shop floor
 * rather than by reading three similar labels. Keyed by tab so adding a tab to the dialog cannot
 * silently shift the icons: a key with no icon simply renders without one.
 */
const ACTION_ICON = {
  [EVENT_DIALOG_TAB_KEYS.JOB_SETTINGS]: TuneRounded,
  [EVENT_DIALOG_TAB_KEYS.PART_SETTINGS]: EditRounded,
  [EVENT_DIALOG_TAB_KEYS.PIN_SETTING]: PushPinRounded
}

/**
 * The three actions at the foot of the panel. Each one opens the settings dialog that the click on
 * the box used to open directly, on the tab that matches what was pressed -- so nothing new is
 * reachable from here, it is just reachable by name instead of by guessing which tab to hunt for.
 *
 * They sit at the foot, away from the data, and each is a full tile rather than a text link: this is
 * pressed at the machine, standing, with gloves on.
 *
 * @param {Object} props Props object
 * @param {function(string): void} props.onOpen Opens the dialog on the given tab key
 * @returns {JSX.Element} The action row
 */
export const CalendarSidePanelActions = ({ onOpen }) => {
  const { t_ } = useSmartTranslation()
  return (
    <>
      {EVENT_DIALOG_TABS.map((tab) => {
        const Icon = ACTION_ICON[tab.key]
        return (
          <button
            key={tab.key}
            type='button'
            className='csp__action'
            onClick={() => onOpen(tab.key)}
          >
            {Icon ? <Icon /> : null}
            <span>{t_(tab.label)}</span>
          </button>
        )
      })}
    </>
  )
}

const Group = ({ heading, children }) => (
  <section className='csp__group'>
    <div className='csp__group-head'>{heading}</div>
    {children}
  </section>
)

const Rows = ({ children }) => <dl className='csp__list'>{children}</dl>

/**
 * One labelled value. Renders nothing at all when there is no value: an empty row would read as a
 * field the planner failed to fill rather than one this kind of entry simply does not have.
 *
 * @param {Object} props Props object
 * @param {string} props.label Name of the field
 * @param {*} props.value Value to show
 * @returns {JSX.Element|null} The row, or nothing when there is no value
 */
const Row = ({ label, value }) => {
  if (value === undefined || value === null || value === '') {
    return null
  }
  return (
    <div className='csp__row'>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  )
}

/**
 * Reads the operators off the event. The planner sends either a list or a single object, and the
 * tooltip coped with both, so this does too rather than "fixing" the shape here.
 *
 * @param {Object} event Calendar event
 * @returns {Array<string>} The names, in order, without empties
 */
const operatorsOf = (event) => {
  const list = event?.extendedProps?.plannerWorkerList
  if (Array.isArray(list)) {
    return list.map((worker) => worker?.workerNameSurname).filter(Boolean)
  }
  return list?.workerNameSurname ? [list.workerNameSurname] : []
}

const CalendarSidePanel = ({ event, unitCriteriaJob, onClose, actions = null }) => {
  const { t_ } = useSmartTranslation()
  const { formatDate, formatDateTime } = useDateFormatter()

  if (!event) {
    return null
  }

  const props = event.extendedProps || {}

  const head = (title) => (
    <header className='csp__head'>
      <div>
        <div className='csp__eyebrow'>{t_('Selected job')}</div>
        <div className='csp__title'>{title}</div>
      </div>
      <button type='button' className='csp__close' onClick={onClose} aria-label={t_('Close panel')}>
        ✕
      </button>
    </header>
  )

  // A wait is a queue, so it is shown as one: the panel is wide enough for the table the tooltip
  // already drew, and the heading says whether the rows are parts or orders. Since story #146 each
  // row also answers which fabrication order, which job and which technology the wait belongs to
  // (RN39): the box on the grid only has room for order + job, and the rest is read here.
  if (props.wait) {
    return (
      <aside className='csp' aria-label={t_('Selected job')}>
        {head(t_('Wait'))}
        <div className='csp__body'>
          <table className='csp__table'>
            <thead>
              <tr>
                <th>{unitCriteriaJob ? t_('Part') : t_('Fabrication Order')}</th>
                <th>{t_('FO')}</th>
                <th>{t_('Job')}</th>
                <th>{t_('Technology')}</th>
                <th>{t_('Start')}</th>
                <th>{t_('End')}</th>
              </tr>
            </thead>
            <tbody>
              {(props.waits || []).map((item) => (
                <tr key={item.name + '-' + item.start}>
                  {/* The first column is the queued item, exactly as it always was: the part
                      code, or the order code when there is no part. The three that follow are the
                      detail the box cannot hold -- which order the wait belongs to, which job it
                      precedes and with which technology. */}
                  <td>{item.name}</td>
                  <td>{waitOrderLabel(item)}</td>
                  <td>{waitJobLabel(item)}</td>
                  <td>{waitTechnologyLabel(item, t_)}</td>
                  <td>{formatDateTime(item.start)}</td>
                  <td>{formatDateTime(item.end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </aside>
    )
  }

  // An unavailable device or a forecast: the planner only knows a name and a couple of dates, and
  // a forecast does not even have a start.
  if (!props.jobEvent) {
    return (
      <aside className='csp' aria-label={t_('Selected job')}>
        {head(props.forecast ? t_('Device not available') : props.jobEventName || t_('Event'))}
        <div className='csp__body'>
          <Group heading={t_('When')}>
            <Rows>
              {props.forecast ? null : <Row label={t_('Event')} value={props.jobEventName} />}
              {props.forecast ? null : <Row label={t_('Date start')} value={formatDateTime(event.start)} />}
              <Row
                label={t_(props.forecast ? 'Device not available until' : 'Date end')}
                value={formatDateTime(event.end)}
              />
            </Rows>
          </Group>
        </div>
      </aside>
    )
  }

  const operators = operatorsOf(event)

  return (
    <aside className='csp' aria-label={t_('Selected job')}>
      {head([props.currentJob, props.machineName].filter(Boolean).join(' · '))}

      <div className='csp__body'>
        <Group heading={t_('Fabrication order details')}>
          <Rows>
            <Row label={t_('FO')} value={props.nameFO} />
            <Row
              label={t_('Delivery date')}
              value={props.deliveryDateFO ? formatDate(props.deliveryDateFO) : ''}
            />
            {unitCriteriaJob
              ? (
                <>
                  <Row label={t_('Part name')} value={props.partName} />
                  <Row label={t_('Status')} value={props.status} />
                </>
                )
              : <Row label={t_('Part reference')} value={props.partReference} />}
          </Rows>

          {/* Only the three steps the event actually carries. The rest of the workflow is not
              known here, and drawing a full chain would be inventing it. */}
          <div className='csp__chain'>
            <div className='csp__step'>
              <small>{t_('Previous')}</small>
              <b>{props.previousJob || '—'}</b>
            </div>
            <div className='csp__step csp__step--now'>
              <small>{t_('This job')}</small>
              <b>{props.currentJob || '—'}</b>
            </div>
            <div className='csp__step'>
              <small>{t_('Next')}</small>
              <b>{props.nextJob || '—'}</b>
            </div>
          </div>
          {props.currentJobNum && props.totalJobCount
            ? (
              <div className='csp__position'>
                {t_('Step {{current}} of {{total}}', {
                  current: props.currentJobNum,
                  total: props.totalJobCount
                })}
              </div>
              )
            : null}
        </Group>

        <Group heading={t_('Job details')}>
          <Rows>
            <Row label={t_('Date start')} value={formatDateTime(event.start)} />
            <Row label={t_('Date end')} value={formatDateTime(event.end)} />
            <Row label={t_('Preparation time')} value={props.preparationTime} />
            {unitCriteriaJob
              ? null
              : (
                <>
                  <Row label={t_('Preparation time 1st part')} value={props.preparationTimeFirstPart} />
                  <Row label={t_('Preparation time next parts')} value={props.preparationTimeNextParts} />
                </>
                )}
          </Rows>
        </Group>

        <Group heading={t_('Asset and selected operators')}>
          <Rows>
            <Row label={t_('Asset name')} value={props.machineName} />
          </Rows>
          {/* Every operator, visible at once. The tooltip hid all but the first behind a popper,
              which is exactly the kind of hover-to-discover the panel exists to remove. */}
          {operators.length === 0
            ? <Rows><Row label={t_('Operators')} value='0' /></Rows>
            : (
              <div className='csp__chips'>
                {operators.map((name) => <span className='csp__chip' key={name}>{name}</span>)}
              </div>
              )}
        </Group>

        <Group heading={t_('Tools and fixtures')}>
          <Rows>
            <Row label={t_('Tool reference')} value={props.toolReferences?.toString()} />
            {unitCriteriaJob ? <Row label={t_('Fixture')} value={props.fixtureName} /> : null}
          </Rows>
        </Group>
      </div>

      {actions ? <div className='csp__actions'>{actions}</div> : null}
    </aside>
  )
}

export default CalendarSidePanel
